import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, addDoc, query, where, onSnapshot, orderBy, limit, serverTimestamp, Timestamp, updateDoc, doc, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import { logger } from '../utils/logger';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

/**
 * Système de chat en temps réel
 * Supporte :
 * - Chat entre client et entreprise
 * - Chat pour réservations taxi (client ↔ chauffeur)
 * - Notifications en temps réel
 * - Historique des conversations
 */
export const ChatProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Charger les conversations de l'utilisateur
  useEffect(() => {
    if (!currentUser) {
      setConversations([]);
      return;
    }

    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', currentUser.uid),
      orderBy('lastMessageAt', 'desc')
    );

    const unsubscribe = onSnapshot(conversationsQuery, (snapshot) => {
      const convos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setConversations(convos);
      
      // Calculer le nombre de messages non lus
      const totalUnread = convos.reduce((sum, conv) => {
        const unread = conv.unreadCount?.[currentUser.uid] || 0;
        return sum + unread;
      }, 0);
      setUnreadCount(totalUnread);
    }, (error) => {
      logger.error(error, { context: { action: 'loadConversations' } });
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Marquer une conversation comme lue
  const markConversationAsRead = useCallback(async (conversationId) => {
    if (!currentUser || !conversationId) return;

    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        [`unreadCount.${currentUser.uid}`]: 0
      });
    } catch (error) {
      logger.error(error, { context: { action: 'markConversationAsRead' } });
    }
  }, [currentUser]);

  // Charger les messages d'une conversation
  useEffect(() => {
    if (!activeConversation || !currentUser) {
      setMessages([]);
      return;
    }

    setLoading(true);
    const messagesQuery = query(
      collection(db, 'conversations', activeConversation.id, 'messages'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const msgs = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .reverse(); // Inverser pour afficher du plus ancien au plus récent
      
      setMessages(msgs);
      setLoading(false);

      // Marquer les messages comme lus
      markConversationAsRead(activeConversation.id);
    }, (error) => {
      logger.error(error, { context: { action: 'loadMessages' } });
      setLoading(false);
    });

    return () => unsubscribe();
  }, [activeConversation, currentUser, markConversationAsRead]);

  // Créer ou récupérer une conversation
  const getOrCreateConversation = useCallback(async (otherUserId, type = 'general', metadata = {}) => {
    if (!currentUser || !otherUserId) return null;

    try {
      // Chercher une conversation existante
      const existingQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', currentUser.uid)
      );

      const snapshot = await getDocs(existingQuery);
      const existing = snapshot.docs.find(doc => {
        const data = doc.data();
        return data.participants.includes(otherUserId) && 
               data.participants.length === 2 &&
               data.type === type;
      });

      if (existing) {
        const conv = { id: existing.id, ...existing.data() };
        setActiveConversation(conv);
        return conv;
      }

      // Créer une nouvelle conversation
      const conversationData = {
        participants: [currentUser.uid, otherUserId],
        type, // 'general', 'taxi_booking', 'business_inquiry'
        metadata,
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        unreadCount: {
          [currentUser.uid]: 0,
          [otherUserId]: 0
        }
      };

      const docRef = await addDoc(collection(db, 'conversations'), conversationData);
      const newConv = { id: docRef.id, ...conversationData };
      setActiveConversation(newConv);


      return newConv;
    } catch (error) {
      logger.error(error, { context: { action: 'getOrCreateConversation' } });
      return null;
    }
  }, [currentUser]);

  // Envoyer un message
  const sendMessage = useCallback(async (text, conversationId = null) => {
    if (!currentUser || !text.trim()) return null;

    const convId = conversationId || activeConversation?.id;
    if (!convId) {
      logger.warn('No active conversation to send message to');
      return null;
    }

    try {
      const messageData = {
        senderId: currentUser.uid,
        senderName: currentUser.displayName || currentUser.email || 'Utilisateur',
        text: text.trim(),
        createdAt: serverTimestamp(),
        read: false,
        readAt: null
      };

      const messagesRef = collection(db, 'conversations', convId, 'messages');
      const messageRef = await addDoc(messagesRef, messageData);

      // Mettre à jour la conversation
      const conversationRef = doc(db, 'conversations', convId);
      await updateDoc(conversationRef, {
        lastMessageAt: serverTimestamp(),
        lastMessage: text.trim().substring(0, 100) // Premiers 100 caractères
      });

      // Incrémenter le compteur de messages non lus pour l'autre participant
      const conversation = conversations.find(c => c.id === convId);
      if (conversation) {
        const otherParticipant = conversation.participants.find(p => p !== currentUser.uid);
        if (otherParticipant) {
          await updateDoc(conversationRef, {
            [`unreadCount.${otherParticipant}`]: (conversation.unreadCount?.[otherParticipant] || 0) + 1
          });
        }
      }


      return { id: messageRef.id, ...messageData };
    } catch (error) {
      logger.error(error, { context: { action: 'sendMessage' } });
      return null;
    }
  }, [currentUser, activeConversation, conversations]);

  // Créer une conversation pour une réservation taxi
  const createTaxiBookingChat = useCallback(async (bookingId, driverId, driverName) => {
    if (!currentUser) return null;

    return await getOrCreateConversation(driverId, 'taxi_booking', {
      bookingId,
      driverId,
      driverName,
      clientId: currentUser.uid
    });
  }, [currentUser, getOrCreateConversation]);

  // Créer une conversation pour une entreprise
  const createBusinessChat = useCallback(async (businessId, businessName, businessOwnerId) => {
    if (!currentUser) return null;

    return await getOrCreateConversation(businessOwnerId, 'business_inquiry', {
      businessId,
      businessName,
      clientId: currentUser.uid
    });
  }, [currentUser, getOrCreateConversation]);

  const value = {
    conversations,
    activeConversation,
    messages,
    loading,
    unreadCount,
    setActiveConversation,
    getOrCreateConversation,
    sendMessage,
    markConversationAsRead,
    createTaxiBookingChat,
    createBusinessChat
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

